import React, {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useCallback
} from "react";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import {
  seekFor,
  seekTo,
  seekToLiveEdge
} from "../../helpers/mediaElementSeek";
import { setMediaMuted, setMediaVolume } from "../../helpers/volume";
import { usePlayerStore } from "../../store/usePlayerStore";

type Props = {
  setAudio: Dispatch<SetStateAction<HTMLAudioElement | undefined>>;
  audioRef: RefObject<HTMLAudioElement | undefined>;
};

export const AudioElement: FC<Props> = ({ audioRef, setAudio }) => {
  const { getState, useStoreState } = usePlayerStore();

  const setError = useStoreState.setError();
  const setIsMuted = useStoreState.setIsMuted();
  const setIsPaused = useStoreState.setIsPaused();
  const setIsPlaybackStarted = useStoreState.setIsPlaybackStarted();
  const setPlayer = useStoreState.setPlayer();
  const setVolume = useStoreState.setVolume();

  const { preload } = useEmbedParametersContext();

  const tryAgainFailedAutoplay = useCallback(
    (audio: HTMLAudioElement, options: { throwError?: boolean }) => {
      audio.muted = true;
      return audio.play().catch(err => {
        console.error("Failed to start audio after mute", err);
        audio.muted = false;
        setIsPaused(true);
        setIsPlaybackStarted(false);
        if (options.throwError) {
          throw err;
        }
      });
    },
    [setIsPaused, setIsPlaybackStarted]
  );

  const playAudio = useCallback(
    (
      audio: HTMLAudioElement,
      isAutoplay = false,
      options: { throwError?: boolean } = {}
    ) => {
      setIsPlaybackStarted(true);
      setIsPaused(false);

      if (getState().isLive && audio.seekable.length) {
        seekToLiveEdge(audio);
      }

      return audio.play().catch(() => {
        // iOS requires additional play() call
        return audio.play().catch(err => {
          if (isAutoplay && audio && !audio.muted) {
            return tryAgainFailedAutoplay(audio, options);
          } else {
            console.error("Audio playback failed", err);

            if (isAutoplay) {
              setIsPlaybackStarted(false);
            }
            setError(err);
            setIsPaused(true);

            if (options.throwError) {
              throw err;
            }
          }
        });
      });
    },
    [
      getState,
      setError,
      setIsPaused,
      setIsPlaybackStarted,
      tryAgainFailedAutoplay
    ]
  );

  const pauseAudio = useCallback(
    (audio: HTMLAudioElement) => {
      setIsPaused(true);
      audio.pause();
    },
    [setIsPaused]
  );

  const setPlaybackRate = useCallback(
    (video: HTMLAudioElement, playbackRate: number) => {
      try {
        video.playbackRate = playbackRate;
      } catch (err) {
        console.error(`Failed to set playback rate to ${playbackRate}`, err);
      }
    },
    []
  );

  const handleAudioRef = useCallback(
    (node: HTMLAudioElement | null) => {
      if (node) {
        setIsPaused(node.paused);
        const state = getState();
        node.muted = state.isMuted;
        node.volume = state.volume;
        node.playbackRate = state.playbackRate;
      }
      setAudio(node || undefined);
      audioRef.current = node || undefined;

      const onVolumeMutedError = ({ isMuted, volume }) => {
        setIsMuted(isMuted);
        setVolume(volume);
      };

      setPlayer(
        node
          ? {
              autoplay: () => playAudio(node, true),
              pause: () => pauseAudio(node),
              play: options => playAudio(node, false, options),
              seekFor: amount => seekFor(node, amount),
              seekTo: newCurrentTime => seekTo(node, newCurrentTime),
              setMuted: (isMuted, persist = false) =>
                setMediaMuted(node, isMuted, persist, onVolumeMutedError),
              setPlaybackRate: playbackRate =>
                setPlaybackRate(node, playbackRate),
              setVolume: (volume, persist = false) =>
                setMediaVolume(node, volume, persist, onVolumeMutedError),
              type: "audio"
            }
          : undefined
      );
    },
    [
      setAudio,
      audioRef,
      setPlayer,
      setIsPaused,
      getState,
      setIsMuted,
      setVolume,
      playAudio,
      pauseAudio,
      setPlaybackRate
    ]
  );

  return (
    <audio
      className="simple-audio-element"
      preload={preload || "none"}
      ref={handleAudioRef}
    />
  );
};
